import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const whatsappRef = useRef(null);
  // Ensure body is not clipping fixed elements
  useEffect(() => {
    const original = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = original;
    };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिंदी'},
    { code: 'mr', name: 'मराठी'},
  ];

  // Filter languages based on current page
  const getAvailableLanguages = () => {
    if (location.pathname === '/emi-calculator') {
      return languages.filter(lang => lang.code === 'en');
    }
    return languages;
  };

  // Check if current path contains "/application"
  const isApplicationPage = location.pathname.includes('/application');

  const navigationLinks = [
    { name: 'Personal Loans', path: '/' },
    { name: 'EMI Calculator', path: '/emi-calculator' },
    { name: 'Lending Partners', path: '/lending-partner' },
	{ name: 'Know Financing', path: '/how-to' },
  ];

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);



  const handleWhatsAppClick = () => {
    const phoneNumber = '9662267474';
    const message = 'Hello! I would like to know more about your services.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in a new tab/window
    window.open(whatsappUrl, '_blank');
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigationClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };



  return (
    <>
      <nav className="navbar navbar-expand-sm navbar-light custom-navbar sticky-top">
        <div className="container-fluid d-flex justify-content-between align-items-center flex-wrap">
          {/* Logo */}
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img
              src="/images/snapfuns-logo.png"
              alt="Snap Funds"
              className="logo-img"
            />
          </a>

          {/* Navigation Links - Desktop */}
          {!isMobile && !isApplicationPage && (
            <div className="navbar-nav mx-auto">
              <div className="nav-links-container">
                {navigationLinks.map((link, index) => (
                  <a
                    key={index}
                    href="#"
                    className="nav-link nav-link-animated"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationClick(link.path);
                    }}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && !isApplicationPage && (
            <button
              className="navbar-toggler"
              type="button"
              onClick={toggleMobileMenu}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )}

          {/* Language Dropdown - Desktop */}
          {!isMobile && (
            <div className="language-dropdown" onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
              <button className="dropbtn">
                <img
                  src="/images/globe-02.png"
                  alt="Lang"
                />
                <img
                  src="/images/Arrow.png"
                  alt="Dropdown"
                />
              </button>
              {dropdownOpen && (
                <div className="header-dropdown-content">
                  {getAvailableLanguages().map(lang => (
                    <a
                      key={lang.code}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        i18n.changeLanguage(lang.code);
                      }}
                      className={i18n.language === lang.code ? 'active' : ''}
                    >
                      {lang.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Language Dropdown - Mobile */}
          {isMobile && (
            <div className="language-dropdown-mobile position-relative">
              <button
                className="btn btn-light d-flex align-items-center dropbtn-mobile"
                onClick={toggleDropdown}
              >
                <img
                  src="/images/globe-02.png"
                  alt="Lang"
                />
                <img
                  src="/images/Arrow.png"
                  alt="Dropdown"
                />
              </button>
              {dropdownOpen && (
                <div className="header-dropdown-content-mobile">
                  {getAvailableLanguages().map(lang => (
                    <a
                      key={lang.code}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        i18n.changeLanguage(lang.code);
                      }}
                      className={i18n.language === lang.code ? 'active' : ''}
                    >
                      {lang.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Navigation Menu */}
        {isMobile && mobileMenuOpen && !isApplicationPage && (
          <div className="mobile-nav-menu">
            <div className="mobile-nav-links">
              {navigationLinks.map((link, index) => (
                <a
                  key={index}
                  href="#"
                  className="mobile-nav-link mobile-nav-link-animated"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigationClick(link.path);
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Fixed WhatsApp Button - rendered to body to avoid transform/overflow issues */}
      {!isApplicationPage && createPortal(
        <div
          ref={whatsappRef}
          className="whatsapp-fixed-button"
          onClick={handleWhatsAppClick}
          aria-label="Chat on WhatsApp"
          role="button"
        >
          <img
            src="/images/whatsapp-mobile-icon.png"
            alt="WhatsApp"
            className="whatsapp-fixed-icon"
            draggable={false}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default Navbar;